import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  GetPriceResponseSchemaType,
  GetSignDealPriceResponseSchemaType,
  GetSignPriceResponseSchemaType,
  PriceRequestSchemaType,
  PriceSchemaType,
  SignDealPriceSchemaType,
  SignPriceSchemaType,
} from '@/global/schemas/price.schema';

import { REDUX_SLICE } from '../constants';
import { axiosBaseQuery } from '../utils';

export const priceApi = createApi({
  reducerPath: REDUX_SLICE.PRICE_API,
  tagTypes: ['Prices', 'PricesV1', 'PricesV2', 'RequestPriceV1', 'RequestPriceV2', 'PricesOTCV2'],
  baseQuery: axiosBaseQuery,
  endpoints: builder => ({
    fetchPrices: builder.query<PriceSchemaType, undefined>({
      query: () => ({ path: `v1/prices` }),
      transformResponse: (returnValue: GetPriceResponseSchemaType) => returnValue.data,
      providesTags: (_result, _error) => [{ type: 'Prices' }],
    }),
    fetchPricesV1: builder.mutation<PriceSchemaType, PriceRequestSchemaType>({
      query: data => ({
        method: 'POST',
        path: `v1/price`,
        data,
        version: 'v1',
      }),
      transformResponse: (returnValue: GetPriceResponseSchemaType) => returnValue.data,
      invalidatesTags: (_result, _error) => [{ type: 'PricesV1' }],
    }),
    fetchPricesV2: builder.mutation<PriceSchemaType, PriceRequestSchemaType>({
      query: data => ({
        method: 'POST',
        path: `v1/prices`,
        data: {
          ...data,
          sale: data.nft,
        },
        version: 'v2',
        headers: {
          'x-offchain-address': data.buyerAddress,
        },
      }),
      transformResponse: (returnValue: GetPriceResponseSchemaType) => returnValue.data,
      invalidatesTags: (_result, _error) => [{ type: 'PricesV2' }],
    }),
    fetchPricesOTCV2: builder.mutation<SignDealPriceSchemaType, PriceRequestSchemaType & { otcId: string }>({
      query: ({ otcId, coin, buyerAddress }) => ({
        method: 'POST',
        path: `v1/prices/deals/${otcId}`,
        data: { coin },
        version: 'v2',
        headers: {
          'x-offchain-address': buyerAddress,
        },
      }),
      transformResponse: (returnValue: GetSignDealPriceResponseSchemaType) => returnValue.data,
    }),
    requestPriceV1: builder.mutation<SignPriceSchemaType, PriceRequestSchemaType>({
      query: data => {
        const { isCrossmint } = data;
        const headers = isCrossmint ? { 'x-offchain-crossmint': true } : {};
        return {
          method: 'POST',
          path: `v1/price/get`,
          data,
          version: 'v1',
          headers: { ...headers },
        };
      },
      transformResponse: (returnValue: GetSignPriceResponseSchemaType) => returnValue.data,
      invalidatesTags: (_result, _error) => [{ type: 'RequestPriceV1' }],
    }),
    requestPriceV2: builder.mutation<SignPriceSchemaType, PriceRequestSchemaType>({
      query: data => {
        const { isCrossmint } = data;
        const headers = isCrossmint ? { 'x-offchain-crossmint': true } : {};
        return {
          method: 'POST',
          path: `v1/prices`,
          data,
          version: 'v2',
          headers: { 'x-offchain-address': data.buyerAddress, ...headers },
        };
      },
      transformResponse: (returnValue: GetSignPriceResponseSchemaType) => returnValue.data,
      invalidatesTags: (_result, _error) => [{ type: 'RequestPriceV2' }],
    }),
  }),
});

export const {
  useFetchPricesQuery,
  useFetchPricesV1Mutation,
  useFetchPricesV2Mutation,
  useFetchPricesOTCV2Mutation,
  useRequestPriceV1Mutation,
  useRequestPriceV2Mutation,
} = priceApi;
